// Supabase access for the Estimating module (matches estimating_schema_migration.sql).
import { supabase } from '@/lib/supabase';

export const LINE_TABLES = {
  priced_boq: 'estimate_priced_boq_lines',
  cost_budget: 'estimate_cost_budget_lines',
  tender_summary: 'estimate_tender_summary_lines',
};

export async function listImports(projectId) {
  const { data, error } = await supabase
    .from('estimate_imports').select('*').eq('project_id', projectId)
    .order('import_type').order('version_number', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getImport(importId) {
  const { data, error } = await supabase.from('estimate_imports').select('*').eq('id', importId).maybeSingle();
  if (error) throw error;
  return data;
}

export async function getLines(imp) {
  const { data, error } = await supabase
    .from(LINE_TABLES[imp.import_type]).select('*').eq('import_id', imp.id).order('display_order');
  if (error) throw error;
  return data || [];
}

export async function getConfirmedTenderSummary(projectId) {
  const { data, error } = await supabase
    .from('estimate_imports').select('*')
    .eq('project_id', projectId).eq('import_type', 'tender_summary').eq('status', 'confirmed')
    .maybeSingle();
  if (error) throw error;
  return data;
}

const safeName = (name, fallback) => (name || fallback).replace(/[^a-zA-Z0-9._-]+/g, '_');
const stamp = () => new Date().toISOString().replace(/[:.]/g, '-');

export async function uploadImportFile({ file, organisationId, projectId, importType }) {
  const filename = safeName(file.name, 'import.xlsx');
  const path = `${organisationId}/${projectId}/${importType}/${stamp()}-${filename}`;
  const { error } = await supabase.storage.from('estimate-files').upload(path, file, {
    upsert: false,
    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  if (error) throw error;
  return { path, filename };
}

export async function createImport(payload) {
  const { data, error } = await supabase.from('estimate_imports').insert([payload]).select().single();
  if (error) throw error;
  return data;
}

export async function insertLines(parsed, imp, { chunkSize = 400 } = {}) {
  const ident = { import_id: imp.id, project_id: imp.project_id, organisation_id: imp.organisation_id };
  const rows = parsed.rows.map((r, idx) => {
    const base = { ...ident, display_order: idx, source_row: r.source_row };
    if (parsed.import_type === 'priced_boq') {
      return { ...base, trade: r.trade, cost_code: r.cost_code, item_ref: r.item_ref, description: r.description,
        unit: r.unit, quantity: r.quantity, selling_rate: r.selling_rate, selling_total: r.selling_total };
    }
    if (parsed.import_type === 'cost_budget') {
      return { ...base, trade: r.trade, cost_code: r.cost_code, item_ref: r.item_ref, description: r.description,
        labour: r.labour, materials: r.materials, subcontractor: r.subcontractor, plant: r.plant, other: r.other, total_cost: r.total_cost };
    }
    return { ...base, component: r.component, description: r.description || r.component_label, amount: r.amount };
  });
  for (let i = 0; i < rows.length; i += chunkSize) {
    const { error } = await supabase.from(LINE_TABLES[parsed.import_type]).insert(rows.slice(i, i + chunkSize));
    if (error) throw error;
  }
  return rows.length;
}

// BOQ-A: draft-only rate edit (RLS est_pbl_update + estimate_lines_guard enforce owner/admin + draft parent)
export async function updateLineRate(line, rate) {
  const qty = Number(line.quantity) || 0;
  const total = Math.round(qty * rate * 100) / 100;
  const { data, error } = await supabase.from('estimate_priced_boq_lines')
    .update({ selling_rate: rate, selling_total: total }).eq('id', line.id).select('id, selling_rate, selling_total');
  if (error) throw error;
  if (!data?.length) throw new Error('Rate not saved — this estimate is read-only or you lack permission');
  return data[0];
}

export async function confirmImport({ importId, overrideReason = null }) {
  const { data, error } = await supabase.rpc('confirm_estimate_import', {
    _import_id: importId, _override_reason: overrideReason?.trim() || null,
  });
  if (error) throw error;
  return data;
}

export async function rejectImport({ importId, reason }) {
  const { data, error } = await supabase.rpc('reject_estimate_import', { _import_id: importId, _reason: reason });
  if (error) throw error;
  return data;
}

export async function discardDraft(importId) {
  const { error } = await supabase.rpc('discard_estimate_draft', { _import_id: importId });
  if (error) throw error;
}

export async function signedUrl(bucket, path) {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 120);
  if (error) throw error;
  return data.signedUrl;
}

// ---- Reference documents (original QS workbooks) ---------------------------
export async function listReferenceDocs(projectId) {
  const { data, error } = await supabase
    .from('estimate_reference_docs').select('*').eq('project_id', projectId).order('uploaded_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function uploadReferenceDoc({ file, organisationId, projectId, userId, sha256, description, linkedImportId }) {
  const filename = safeName(file.name, 'workbook');
  const path = `${organisationId}/${projectId}/${stamp()}-${filename}`;
  const { error: upErr } = await supabase.storage.from('qs-workbooks').upload(path, file, { upsert: false });
  if (upErr) throw upErr;
  const { data, error } = await supabase.from('estimate_reference_docs').insert([{
    project_id: projectId, organisation_id: organisationId, file_name: filename,
    storage_bucket: 'qs-workbooks', storage_path: path, file_size_bytes: file.size, file_sha256: sha256,
    description: description || null, linked_import_id: linkedImportId || null, uploaded_by: userId,
  }]).select().single();
  if (error) {
    await supabase.storage.from('qs-workbooks').remove([path]);
    throw error;
  }
  return data;
}

export async function removeReferenceDoc(doc) {
  const { data, error } = await supabase.rpc('remove_reference_doc', { _doc_id: doc.id });
  if (error) throw error;
  if (data === 'deleted') await supabase.storage.from('qs-workbooks').remove([doc.storage_path]);
  return data;
}
